import { IconPlusCircle } from "@/assets/icons/geist/IconPlusCircle";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { Heading3, Heading4 } from "@/components/podkit/typography/Headings";
import { TrackLocations } from "@/hooks/use-segment";
import { useListDomainVerifications, useUpdateSSOConfiguration, useVerifyDomain } from "@/queries/organization-queries";
import { CustomSSOConfigCardV2 } from "@/routes/settings/login/SSOConfigCardV2";
import { SetupSSOModal, type SetupSSOModalStep } from "@/routes/settings/login/SetupSSOModal";
import { DomainVerification } from "@/routes/settings/login/DomainVerification";
import { type FC, useCallback, useMemo, useState } from "react";
import {
    DomainVerificationState,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type CustomSSOSectionProps = {
    organizationId: string;
    configs: SSOConfiguration[];
    deactivateable: boolean;
    editable: boolean;
};

type ModalState = {
    step: SetupSSOModalStep;
    config?: SSOConfiguration;
};

export const CustomSSOSection: FC<CustomSSOSectionProps> = ({ organizationId, configs, deactivateable, editable }) => {
    const { toast } = useToast();
    const [modalState, setModalState] = useState<ModalState | undefined>(undefined);
    // Stores the SSO config ID when opening new domain modal, undefined when closed
    const [newDomainForConfigId, setNewDomainForConfigId] = useState<string | undefined>(undefined);
    const [showDomainInfo, setShowDomainInfo] = useState<string | undefined>(undefined);
    const [verifyingDomainId, setVerifyingDomainId] = useState<string | undefined>(undefined);

    const { data: domainVerifications = [] } = useListDomainVerifications();

    const pendingVerifications = useMemo(() => {
        return domainVerifications.filter((v) => v.state === DomainVerificationState.PENDING);
    }, [domainVerifications]);

    const verifyDomain = useVerifyDomain();
    const updateSSOConfig = useUpdateSSOConfiguration();

    const openSetupModal = useCallback((step: SetupSSOModalStep, config?: SSOConfiguration) => {
        setModalState({ step, config });
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalState(undefined);
    }, []);

    const handleCreateNewSSOConfig = useCallback(() => {
        openSetupModal("configure-sso");
    }, [openSetupModal]);

    const handleEdit = useCallback(
        (config: SSOConfiguration) => {
            openSetupModal("configure-sso", config);
        },
        [openSetupModal],
    );

    const handleNewDomain = useCallback((configId: string) => {
        setNewDomainForConfigId(configId);
    }, []);

    const handleCloseNewDomainModal = useCallback(() => {
        setNewDomainForConfigId(undefined);
    }, []);

    // Auto-add domain to SSO config when a new domain verification is created
    const handleDomainCreated = useCallback(
        async (domain: string) => {
            if (!newDomainForConfigId) {
                return;
            }

            // Find the config to get current domains
            const config = configs.find((c) => c.id === newDomainForConfigId);
            if (!config) {
                return;
            }

            // Get current email domains from the config
            const currentDomains =
                config.emailDomains && config.emailDomains.length > 0
                    ? config.emailDomains
                    : config.emailDomain
                      ? [config.emailDomain]
                      : [];

            // Add the new domain if not already present
            if (currentDomains.includes(domain)) {
                return;
            }

            const newDomains = [...currentDomains, domain];

            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: newDomainForConfigId,
                        emailDomains: newDomains,
                    }),
                );
                toast({
                    title: `Added ${domain} to SSO configuration`,
                });
            } catch (error) {
                toast({
                    title: "Failed to add domain to SSO configuration",
                    description: formatError(error),
                });
            }
        },
        [configs, newDomainForConfigId, toast, updateSSOConfig],
    );

    const handleVerifyPendingDomain = useCallback(
        async (verification: (typeof domainVerifications)[0]) => {
            setVerifyingDomainId(verification.id);
            try {
                const result = await verifyDomain.mutateAsync(verification);
                const title =
                    result?.state === DomainVerificationState.VERIFIED
                        ? `${verification.domain} is verified.`
                        : `${verification.domain} is not yet verified. Please try again later.`;

                toast({ title });
            } catch (error) {
                toast({
                    title: "Failed to verify domain.",
                    description: formatError(error),
                });
            } finally {
                setVerifyingDomainId(undefined);
            }
        },
        [toast, verifyDomain],
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <Heading3>Single Sign On</Heading3>
                <div className="ml-auto">
                    <Button
                        disabled={!editable}
                        variant="secondary"
                        size="sm"
                        LeadingIcon={IconPlusCircle}
                        onClick={handleCreateNewSSOConfig}
                        data-tracking-id="create-custom-sso"
                    >
                        New SSO
                    </Button>
                </div>
            </div>

            {configs.length === 0 ? (
                <Card className="flex w-full flex-col gap-2 bg-surface-glass p-4" variant="bordered">
                    <Text className="text-base text-content-primary">No custom SSO configurations yet.</Text>
                    <Text className="text-sm text-content-secondary">
                        Create an SSO configuration to restrict access based on email domain.
                    </Text>
                </Card>
            ) : (
                <div className="flex flex-col gap-3">
                    {configs.map((config) => (
                        <CustomSSOConfigCardV2
                            key={config.id}
                            config={config}
                            deactivateable={deactivateable}
                            editable={editable}
                            onEdit={handleEdit}
                            domainVerifications={domainVerifications}
                            onNewDomain={handleNewDomain}
                        />
                    ))}
                </div>
            )}

            {/* Pending domain verifications section */}
            {pendingVerifications.length > 0 && (
                <div className="flex flex-col gap-3">
                    <Heading4>Email domains pending verification</Heading4>
                    <div className="flex w-full flex-col">
                        {pendingVerifications.map((verification) => (
                            <div key={verification.id} className="flex items-center justify-between py-2">
                                <Text className="text-base text-content-primary">{verification.domain}</Text>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setShowDomainInfo(verification.domain)}
                                        data-tracking-id="show-pending-domain-info"
                                    >
                                        Show
                                    </Button>
                                    <Button
                                        disabled={!editable || verifyingDomainId !== undefined}
                                        size="sm"
                                        className="w-16"
                                        onClick={() => handleVerifyPendingDomain(verification)}
                                        loading={verifyingDomainId === verification.id}
                                        data-tracking-id="verify-pending-domain"
                                    >
                                        Verify
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {modalState && (
                <SetupSSOModal
                    organizationId={organizationId}
                    config={modalState.config}
                    step={modalState.step}
                    onClose={handleCloseModal}
                />
            )}

            {/* New domain modal */}
            {newDomainForConfigId && (
                <Dialog open onOpenChange={(open) => !open && handleCloseNewDomainModal()}>
                    <DialogContent
                        className="flex max-w-[600px] flex-col p-6"
                        data-track-location={TrackLocations.SetupSSOModal}
                    >
                        <DomainVerification
                            organizationId={organizationId}
                            onCancel={handleCloseNewDomainModal}
                            onDomainCreated={handleDomainCreated}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {/* Show domain info modal */}
            {showDomainInfo && (
                <Dialog open onOpenChange={(open) => !open && setShowDomainInfo(undefined)}>
                    <DialogContent
                        className="flex max-w-[600px] flex-col p-6"
                        data-track-location={TrackLocations.SetupSSOModal}
                    >
                        <DomainVerification
                            organizationId={organizationId}
                            domain={showDomainInfo}
                            onCancel={() => setShowDomainInfo(undefined)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};
